<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { Button, type ButtonVariants } from '@/components/shadcn'

type StyleType =
  | 'default'      // 기본 스타일 (primary)
  | 'primary'      // 강조 버튼 (파랑)
  | 'secondary'    // 보조 버튼 (회색)
  | 'success'      // 성공 버튼 (초록)
  | 'warning'      // 경고 버튼 (주황)
  | 'danger'       // 위험 버튼 (빨강)
  | 'outline'      // 아웃라인 버튼
  | 'ghost'        // 고스트 버튼
  | 'link'         // 링크 스타일
  | 'sm'           // 작은 크기
  | 'lg'           // 큰 크기
  | 'icon'         // 아이콘 전용

const props = withDefaults(
  defineProps<{
    /** 버튼 타입 */
    type?: 'button' | 'submit' | 'reset'
    /** 비활성화 상태 */
    disabled?: boolean
    /** 로딩 상태 */
    loading?: boolean
    /** shadcn variant */
    variant?: ButtonVariants['variant']
    /** shadcn size */
    size?: ButtonVariants['size']
    /** 추가 CSS 클래스 */
    class?: string
    /** 스타일 프리셋 (variant + size 조합) */
    styletype?: StyleType
  }>(),
  {
    type: 'button',
    disabled: false,
    loading: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

/**
 * styletype -> variant/size 매핑
 */
const STYLE_PRESETS: Record<StyleType, { variant: ButtonVariants['variant']; size?: ButtonVariants['size']; class?: string }> = {
  default: { 
    variant: 'default',
  },
  primary: { 
    variant: 'default',
  },
  secondary: { 
    variant: 'secondary',
  },
  success: { 
    variant: 'default',
    class: 'bg-green-600 hover:bg-green-700',
  },
  warning: { 
    variant: 'default',
    class: 'bg-amber-500 hover:bg-amber-600',
  },
  danger: { 
    variant: 'destructive',
  },
  outline: { 
    variant: 'outline',
  },
  ghost: { 
    variant: 'ghost',
  },
  link: { 
    variant: 'link',
  },
  sm: { 
    variant: 'default',
    size: 'sm',
  },
  lg: { 
    variant: 'default',
    size: 'lg',
  },
  icon: { 
    variant: 'default',
    size: 'icon',
  },
}

const preset = computed(() => {
  const styleKey = props.styletype || 'default'
  return STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
})

const finalVariant = computed(() => props.variant || preset.value?.variant)
const finalSize = computed(() => props.size || preset.value?.size || 'sm')
const finalClass = computed(() => [preset.value?.class, props.class].filter(Boolean).join(' '))

const isDisabled = computed(() => props.disabled || props.loading)

const handleClick = (event: MouseEvent) => {
  if (!isDisabled.value) {
    emit('click', event)
  }
}
</script>

<template>
  <Button
    :type="type"
    :variant="finalVariant"
    :size="finalSize"
    :class="finalClass"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <Loader2 v-if="loading" class="mr-2 h-4 w-4 animate-spin" />
    <slot />
  </Button>
</template>

<style scoped>
/* ========================================
   패턴 10: Button 향상 효과
   ======================================== */
:deep(button) {
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

:deep(button:not(:disabled):hover) {
  box-shadow: 0 0 0 0 transparent;
}

:deep(button:not(:disabled):active) {
  box-shadow: inset 0 1px 2px rgb(0 0 0 / 0.08);
}
</style>
